import type { PropsWithChildren, FC } from "react";
import { useState, useEffect } from "react";
import KeycloakService, { type KeycloakAuthState } from "../../utils/auth/keycloakService";
import TomanAnimatedLogo from "../../components/TomanAnimatedLogo";
import { keycloakContext } from "./keycloakContext";
import { captureException } from "../../utils/sentry/sentryConfig";

const KeycloakProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [authState, setAuthState] = useState<KeycloakAuthState>({
    isAuthenticated: false,
    token: null,
    refreshToken: null,
    isLoading: true,
  });

  const keycloakService = KeycloakService.getInstance();

  useEffect(() => {
    const initKeycloak = async () => {
      try {
        await keycloakService.initialize();
        setAuthState(keycloakService.getAuthState());
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize Keycloak:", error);
        captureException(error, {
          tags: { error_type: "keycloak_provider_initialization_failed" },
          level: "error",
        });
        setIsInitialized(true);
      }
    };

    initKeycloak();

    const unsubscribe = keycloakService.subscribe((newAuthState) => {
      setAuthState(newAuthState);
    });

    return unsubscribe;
  }, [keycloakService]);

  if (!isInitialized) {
    return <TomanAnimatedLogo />;
  }

  return (
    <keycloakContext.Provider value={{ authState, keycloakService, isInitialized }}>
      {children}
    </keycloakContext.Provider>
  );
};

export default KeycloakProvider;
